/**
 * The Rule 502(b) information package of an issuer (17 CFR 230.502(b); S-30): the offering amount
 * it declares, the bad-actor inquiry it rests on (506(e)), and the documents of its vault (the
 * package, the exhibits and a disclosure of past events). Below, the questions purchasers asked and
 * the answers waiting for a determination.
 *
 * The documents themselves, and the statements in them, are counsel's and the accountant's: this
 * screen keeps them, dates them and refuses to publish a package that cannot stand.
 */
import { useCallback, useEffect, useState } from 'react';
import { limitToCents } from '../../lib/amount';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import OfferingQuestionsPanel from '../../components/manager/OfferingQuestionsPanel';
import SophisticationPanel from '../../components/manager/SophisticationPanel';
import regDStaffService, {
  type Issuer,
  type IssuerDetail,
  type IssuerDocType,
  type IssuerDocument,
  type PackageFactsBody,
} from '../../services/regDStaff.service';
import offeringDocumentsService from '../../services/offeringDocuments.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import {
  ISSUER_DOC_LABEL,
  ISSUER_DOC_TYPES,
  TIER_LABEL,
  documentDetail,
  documentLine,
  inquiryOption,
  issuerOption,
  parseCoveredPersons,
  slotFrom,
  uploadProblem,
  validAmount,
} from './regDPackage.helpers';

const inputStyle = { border: `1px solid ${colors.border}` };
const card = { background: colors.surface, border: `1px solid ${colors.border}` };

interface Report {
  readonly onDone: (message: string) => void;
  readonly onError: (message: string) => void;
}

function AmountPanel({
  entityId,
  detail,
  onDone,
  onError,
}: Readonly<Report & { entityId: string; detail: IssuerDetail }>): JSX.Element {
  const [amount, setAmount] = useState('');
  const [basis, setBasis] = useState('');
  const [busy, setBusy] = useState(false);
  const latest = detail.offering_amounts[0];
  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      await regDStaffService.declareAmount(entityId, amount.trim(), basis.trim());
      setAmount('');
      setBasis('');
      onDone('The offering amount is recorded.');
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not record the offering amount.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="rounded-2xl p-5 mb-6" style={card}>
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>Offering amount</h2>
      <p className="text-sm mb-3" style={{ color: colors.muted }}>
        The aggregate offering price decides which financial statements the package needs. A change
        is a new entry; nothing is edited.
      </p>
      {latest ? (
        <p className="text-sm mb-3" style={{ color: colors.ink }}>
          {`USD ${latest.amount_usd} · ${TIER_LABEL[latest.tier]} · declared ${formatDateTime(latest.declared_at)}`}
        </p>
      ) : (
        <p className="text-sm mb-3" style={{ color: colors.warning }}>
          No amount is declared, so the package cannot be published.
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label htmlFor="rd-amount" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
            Amount in USD
          </label>
          <input id="rd-amount" value={amount} onChange={(e) => setAmount(limitToCents(e.target.value))}
                 inputMode="decimal" className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="rd-basis" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
            Where the figure comes from
          </label>
          <input id="rd-basis" value={basis} maxLength={500} onChange={(e) => setBasis(e.target.value)}
                 placeholder="The total offering amount of the issuer's Form D."
                 className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
        </div>
      </div>
      <button type="button" disabled={busy || !validAmount(amount) || basis.trim().length < 10}
              onClick={() => { void save(); }}
              className="mt-3 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
              style={{ background: colors.navy, color: '#FFFFFF' }}>
        {busy ? 'Saving…' : 'Record the amount'}
      </button>
    </section>
  );
}

function InquiryPanel({
  entityId,
  detail,
  onDone,
  onError,
}: Readonly<Report & { entityId: string; detail: IssuerDetail }>): JSX.Element {
  const [persons, setPersons] = useState('');
  const [result, setResult] = useState<'none' | 'disclosed'>('none');
  const [disclosure, setDisclosure] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const disclosures = detail.documents.filter((d) => d.doc_type === 'bad_actor_disclosure');
  const covered = parseCoveredPersons(persons);
  const ready = covered.length > 0 && (result === 'none' || disclosure !== '');
  const latest = detail.inquiries[0];
  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      await regDStaffService.recordInquiry(entityId, {
        covered_persons: covered,
        result,
        disclosure_document_id: result === 'disclosed' ? disclosure : null,
        notes: notes.trim() || null,
      });
      setPersons('');
      setNotes('');
      onDone('The inquiry is recorded.');
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not record the inquiry.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="rounded-2xl p-5 mb-6" style={card}>
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>Bad-actor inquiry</h2>
      <p className="text-sm mb-3" style={{ color: colors.muted }}>
        Rule 506(e): the issuer must have made a factual inquiry into whether anyone covered was
        disqualified. If it found an event from before 23 September 2013, upload its description as a
        disclosure document first: every purchaser must acknowledge it.
      </p>
      {latest ? (
        <p className="text-sm mb-3" style={{ color: colors.ink }}>
          {`Latest inquiry: ${formatDateTime(latest.performed_at)}`}{' '}
          <StatusBadge tone={latest.result === 'none' ? 'success' : 'warning'}>
            {latest.result === 'none' ? 'Nothing found' : 'Found a past event'}
          </StatusBadge>{' '}
          {`${latest.covered_persons.length} people covered`}
        </p>
      ) : (
        <p className="text-sm mb-3" style={{ color: colors.warning }}>
          No inquiry is recorded, so the package cannot be published.
        </p>
      )}
      <label htmlFor="rd-persons" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
        People covered, one per line: name, role
      </label>
      <textarea id="rd-persons" value={persons} rows={4} onChange={(e) => setPersons(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm mb-3" style={inputStyle} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="rd-result" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
            What it found
          </label>
          <select id="rd-result" value={result}
                  onChange={(e) => setResult(e.target.value as 'none' | 'disclosed')}
                  className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
            <option value="none">Nothing to disclose</option>
            <option value="disclosed">A past event, described in a disclosure document</option>
          </select>
        </div>
        {result === 'disclosed' && (
          <div>
            <label htmlFor="rd-disclosure" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
              Disclosure document
            </label>
            <select id="rd-disclosure" value={disclosure} onChange={(e) => setDisclosure(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
              <option value="">— choose —</option>
              {disclosures.map((d) => (
                <option key={d.document_id} value={d.document_id}>{documentLine(d)}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <label htmlFor="rd-notes" className="block text-xs font-semibold mt-3 mb-1" style={{ color: colors.inkSoft }}>
        Notes (optional)
      </label>
      <input id="rd-notes" value={notes} maxLength={2000} onChange={(e) => setNotes(e.target.value)}
             className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
      <button type="button" disabled={busy || !ready} onClick={() => { void save(); }}
              className="mt-3 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
              style={{ background: colors.navy, color: '#FFFFFF' }}>
        {busy ? 'Saving…' : 'Record the inquiry'}
      </button>
    </section>
  );
}

function UploadPanel({
  entityId,
  detail,
  onDone,
  onError,
}: Readonly<Report & { entityId: string; detail: IssuerDetail }>): JSX.Element {
  const [docType, setDocType] = useState<IssuerDocType>('reg_d_package');
  const [exhibitName, setExhibitName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [basis, setBasis] = useState<'us_gaap' | 'ifrs_iasb'>('us_gaap');
  const [audited, setAudited] = useState(true);
  const [asOf, setAsOf] = useState('');
  const [inquiryId, setInquiryId] = useState(detail.inquiries[0]?.inquiry_id ?? '');
  const [step, setStep] = useState<string | null>(null);
  const problem = uploadProblem({
    docType,
    exhibitName,
    hasFile: file !== null,
    statementsDate: asOf,
    inquiryId,
  });

  const upload = async (): Promise<void> => {
    if (!file) return;
    const slot = docType === 'exhibit' ? slotFrom(exhibitName) : null;
    const facts: PackageFactsBody | null =
      docType === 'reg_d_package'
        ? { fs_basis: basis, fs_audited: audited, fs_as_of: asOf, bad_actor_inquiry_id: inquiryId }
        : null;
    try {
      setStep('Asking storage for a one-time upload link…');
      const presigned = await regDStaffService.presignDocument(entityId, docType, slot);
      setStep('Uploading the file…');
      await offeringDocumentsService.uploadToStorage(presigned.upload_url, file);
      setStep('Checking what arrived…');
      const registered = await regDStaffService.confirmDocument(
        entityId, docType, slot, presigned.object_key, facts,
      );
      setFile(null);
      onDone(`Version ${registered.version} is in the vault, not published yet.`);
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'The upload did not complete.'));
    } finally {
      setStep(null);
    }
  };

  return (
    <section className="rounded-2xl p-5 mb-6" style={card}>
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>Add a document</h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        Everything an issuer publishes about its offering is delivered through the vault, to every
        purchaser. A new file is a new version; nothing becomes current until it is published.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="rd-type" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
            Document
          </label>
          <select id="rd-type" value={docType} disabled={step !== null}
                  onChange={(e) => setDocType(e.target.value as IssuerDocType)}
                  className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
            {ISSUER_DOC_TYPES.map((t) => (
              <option key={t} value={t}>{ISSUER_DOC_LABEL[t]}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="rd-file" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
            PDF
          </label>
          <input id="rd-file" type="file" accept="application/pdf" disabled={step !== null}
                 onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="w-full text-sm" />
        </div>
        {docType === 'exhibit' && (
          <div className="sm:col-span-2">
            <label htmlFor="rd-exhibit" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
              What the exhibit is (a later version of the same name replaces it)
            </label>
            <input id="rd-exhibit" value={exhibitName} maxLength={60}
                   onChange={(e) => setExhibitName(e.target.value)}
                   placeholder="Operating agreement"
                   className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
          </div>
        )}
        {docType === 'reg_d_package' && (
          <>
            <div>
              <label htmlFor="rd-basis-fs" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
                Accounting basis of the statements
              </label>
              <select id="rd-basis-fs" value={basis}
                      onChange={(e) => setBasis(e.target.value as 'us_gaap' | 'ifrs_iasb')}
                      className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
                <option value="us_gaap">US GAAP</option>
                <option value="ifrs_iasb">IFRS as issued by the IASB</option>
              </select>
            </div>
            <div>
              <label htmlFor="rd-asof" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
                Date of the balance sheet
              </label>
              <input id="rd-asof" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)}
                     className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle} />
            </div>
            <label className="flex items-center gap-2 text-sm" style={{ color: colors.ink }}>
              <input type="checkbox" checked={audited} onChange={(e) => setAudited(e.target.checked)} />
              <span>The statements are audited</span>
            </label>
            <div>
              <label htmlFor="rd-inquiry" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
                Bad-actor inquiry it rests on
              </label>
              <select id="rd-inquiry" value={inquiryId} onChange={(e) => setInquiryId(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
                <option value="">— choose —</option>
                {detail.inquiries.map((i) => (
                  <option key={i.inquiry_id} value={i.inquiry_id}>
                    {inquiryOption(i)}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>
      {problem !== null && file !== null && (
        <p className="text-xs mt-2" style={{ color: colors.muted }}>{problem}</p>
      )}
      <div className="flex items-center gap-3 mt-4">
        <button type="button" disabled={problem !== null || step !== null}
                onClick={() => { void upload(); }}
                className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                style={{ background: colors.navy, color: '#FFFFFF' }}>
          Upload
        </button>
        {step && <span className="text-sm" style={{ color: colors.muted }}>{step}</span>}
      </div>
    </section>
  );
}

function VaultRow({
  document,
  onDone,
  onError,
}: Readonly<Report & { document: IssuerDocument }>): JSX.Element {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const publish = async (): Promise<void> => {
    setBusy(true);
    try {
      await offeringDocumentsService.publish(document.document_id, reason.trim());
      setOpen(false);
      setReason('');
      onDone(`${documentLine(document)} is published.`);
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not publish the document.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="rounded-xl p-4" style={{ border: `1px solid ${colors.border}` }}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium" style={{ color: colors.ink }}>{documentLine(document)}</div>
          <div className="text-xs mt-0.5" style={{ color: colors.muted }}>
            {documentDetail(document)}
          </div>
          <div className="text-xs mt-1 font-mono" style={{ color: colors.mutedSoft }}>
            {document.file_hash.slice(0, 16)}…
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusBadge tone={document.is_published ? 'success' : 'neutral'}>
            {document.is_published ? 'Published' : 'Not published'}
          </StatusBadge>
          {!document.is_published && (
            <button type="button" onClick={() => setOpen((v) => !v)}
                    className="text-xs px-3 py-1.5 rounded-md font-semibold"
                    style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
              {open ? 'Cancel' : 'Publish'}
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
          <p className="text-sm mb-3" style={{ color: colors.inkSoft }}>
            From now on every purchaser of this issuer must open and acknowledge this version before
            signing; one who acknowledged an earlier version is asked again.
          </p>
          <label htmlFor={`rd-pub-${document.document_id}`} className="block text-xs font-semibold mb-1"
                 style={{ color: colors.inkSoft }}>
            What changed, or why now?
          </label>
          <input id={`rd-pub-${document.document_id}`} value={reason} disabled={busy} maxLength={500}
                 onChange={(e) => setReason(e.target.value)}
                 className="w-full px-3 py-2 rounded-lg text-sm mb-3" style={inputStyle} />
          <button type="button" disabled={busy || reason.trim().length < 4}
                  onClick={() => { void publish(); }}
                  className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                  style={{ background: colors.navy, color: '#FFFFFF' }}>
            {busy ? 'Publishing…' : 'Publish this version'}
          </button>
        </div>
      )}
    </li>
  );
}

export default function RegDPackage(): JSX.Element {
  const [issuers, setIssuers] = useState<Issuer[]>([]);
  const [entityId, setEntityId] = useState('');
  const [detail, setDetail] = useState<IssuerDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    regDStaffService
      .listIssuers()
      .then(setIssuers)
      .catch((e: unknown) => setError(getApiErrorMessage(e, 'Could not load the issuers.')));
  }, []);

  const reload = useCallback(async () => {
    if (!entityId) {
      setDetail(null);
      return;
    }
    try {
      setDetail(await regDStaffService.getIssuer(entityId));
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load this issuer.'));
    }
  }, [entityId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const done = (message: string): void => {
    setError(null);
    setFlash(message);
    void reload();
  };
  const failed = (message: string): void => {
    setFlash(null);
    setError(message);
  };

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Rule 502(b) package"
        subtitle="What a purchaser who is not accredited must be given before they buy, per issuer."
      />
      {error && (
        <div className="mb-4 p-3 rounded-lg text-sm" role="alert"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          {error}
        </div>
      )}
      {flash && (
        <div className="mb-4 p-3 rounded-lg text-sm"
             style={{ background: colors.successBg, border: `1px solid ${colors.successBorder}`, color: colors.success }}>
          {flash}
        </div>
      )}

      <section className="rounded-2xl p-5 mb-6" style={card}>
        <label htmlFor="rd-issuer" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
          Issuer
        </label>
        <select id="rd-issuer" value={entityId} onChange={(e) => setEntityId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}>
          <option value="">— select issuer —</option>
          {issuers.map((i) => (
            <option key={i.legal_entity_id} value={i.legal_entity_id}>
              {issuerOption(i)}
            </option>
          ))}
        </select>
      </section>

      {entityId && detail && (
        <>
          <AmountPanel entityId={entityId} detail={detail} onDone={done} onError={failed} />
          <InquiryPanel entityId={entityId} detail={detail} onDone={done} onError={failed} />
          <UploadPanel entityId={entityId} detail={detail} onDone={done} onError={failed} />
          {detail.documents.length > 0 && (
            <section className="rounded-2xl p-5 mb-6" style={card}>
              <h2 className="text-lg font-semibold mb-3" style={{ color: colors.ink }}>In the vault</h2>
              <ul className="space-y-3">
                {detail.documents.map((d) => (
                  <VaultRow key={d.document_id} document={d} onDone={done} onError={failed} />
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      <OfferingQuestionsPanel />
      <SophisticationPanel />
    </div>
  );
}
